import { ButtonComponent } from '@/components/commons'
import { notificationIcon } from '@/components/commons/buttons/styles'
import { Avatar } from '@/components/commons/images/Avatar.component'
import { Subtitle } from '@/components/commons/typography/Subtitle'
import { Text } from '@/components/commons/typography/Text'
import { useFreebirdToken } from '@/components/pages/freebird/hooks/freeBirdToken'
import { ModalPage } from '@/components/ui/modals/ModalPage.component'
import { useUrlWithExistingParams } from '@/shared/hooks/url'
import { cssClass } from '@/shared/utils'
import { colorsV2 } from '@/styles/v2/vars/vars.colors'
import {
  useInvitationsReceived,
  useNewConnections,
} from 'containers/profile-network/hooks/useConnections'
import {
  useIsOwnProfile,
  useProfile,
} from 'containers/profile/hooks/useProfile'
import { useUser } from 'containers/user/hooks/useUser'
import { BrowserContext } from 'context'
import { useRouter } from 'next/router'
import { useContext, useEffect, useMemo, useState } from 'react'

export const NavigationTabs = () => {
  const { profile } = useProfile()
  const { isMobile } = useContext(BrowserContext)
  const isOwnProfile = useIsOwnProfile()
  const router = useRouter()
  const { isAuth } = useUser()
  const [show, setShow] = useState(false)
  const [fromProfile, setFromProfile] = useState(null)
  const [invitedUserToken, setInvitedUserToken] = useState('')
  const { getFreebirdData } = useFreebirdToken()
  const { invitationsCount } = useInvitationsReceived()
  const { newConnectionsCount } = useNewConnections()
  const { navigateWithCurrentParams } = useUrlWithExistingParams()

  const [wasLogged, setWasLogged] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isAuth) return

    const localData = getFreebirdData()

    if (localData && !invitedUserToken && !fromProfile) {
      const { fromUser, invitedUserToken } = localData

      setFromProfile(fromUser)
      setInvitedUserToken(invitedUserToken)
    }
  }, [isAuth, fromProfile, invitedUserToken, getFreebirdData])

  useEffect(() => {
    if (isAuth) {
      setWasLogged(true)
      return
    }

    setShow(!isAuth)
  }, [isAuth])

  const tryNetworkTour = () => {
    const routeToNetworkTab = () =>
      router.push(`/${profile?.username?.toLocaleLowerCase()}/network`)

    try {
      if (!isOwnProfile) {
        return routeToNetworkTab()
      }

      const toursApi = window?.productFruits?.api?.tours
      // const DESKTOP_TOUR = ###REPLACE_ME_WITH_TOUR_ID###
      const MOBILE_TOUR = 53426
      // const tour = isMobile ? MOBILE_TOUR : DESKTOP_TOUR
      const tour = MOBILE_TOUR // TODO remove this line once the tour is ready for desktop
      const tourObj = toursApi?.getTours()?.find((t) => t.id === tour)
      const notFinished = tourObj?.userState !== 'finished'

      if (notFinished) {
        toursApi?.listen('tour-finished', (e) => {
          if (e === tour) {
            return routeToNetworkTab()
          }
        })

        toursApi?.tryStartTour(tour)
      } else {
        return routeToNetworkTab()
      }
    } catch (error) {
      return routeToNetworkTab()
    }
  }

  const activePath = useMemo(() => {
    const path = router.pathname
    if (path === '/[username]') return '[username]'
    if (path === '/[username]/analytics') return 'analytics'
    if (path.startsWith('/[username]/network')) return 'network'
    return path
  }, [router.pathname])

  const texts = useMemo(() => {
    const isProfileInviting = fromProfile?.username === profile?.username

    return {
      titleEnd: isProfileInviting ? (
        <span className="cl-pink-secondary">
          1 pending message from {profile?.firstName}
        </span>
      ) : (
        `more!`
      ),
      subTitle: isProfileInviting
        ? ''
        : 'Join Creatorland today to unlock new opportunities, education, community, and connections in the creator economy.',
      buttonsText: isProfileInviting
        ? 'New to Creatorland? You’re approved to skip the waitlist!'
        : 'New to Creatorland?',
    }
  }, [fromProfile, profile])

  const showJoinModal = () => setShow(!show)
  const networkNotificationsCount = newConnectionsCount + invitationsCount

  const navigateToAuth = async (url: string) => {
    router.push(navigateWithCurrentParams(url))
  }

  return (
    <>
      <div className="py-16 px-20 flex border-solid--tablet border-x-0 border-b-0 border-t-1 border-secondary">
        <ButtonComponent
          className="flex-1"
          to={`/${profile?.username?.toLocaleLowerCase()}`}
          label="Overview"
          variation={
            activePath === '[username]' ? 'primary' : 'secondary-no-border'
          }
          size="xs"
        />
        <ButtonComponent
          className="flex-1"
          to={`/${profile?.username?.toLocaleLowerCase()}/analytics`}
          onClick={!isAuth && showJoinModal}
          label="Analytics"
          variation={
            activePath === 'analytics' ? 'primary' : 'secondary-no-border'
          }
          size="xs"
        />
        <ButtonComponent
          className="flex-1"
          onClick={!isAuth ? showJoinModal : tryNetworkTour}
          label={isOwnProfile && !isMobile ? 'My Network' : 'Network'}
          variation={
            activePath === 'network' ? 'primary' : 'secondary-no-border'
          }
          size="xs"
          customSize={{ gap: isMobile ? 6 : 10 }}
          iconLeft={
            isOwnProfile && networkNotificationsCount > 0 ? (
              <div>
                <div
                  className={cssClass([
                    notificationIcon,
                    activePath === 'network' ? 'bg-green' : 'bg-danger-light',
                    'top-9 rounded-20 flex items-center justify-center',
                  ])}
                >
                  <Text
                    sizeClass={isMobile ? 'text-9' : 'text-11'}
                    weightClass="font-bold"
                    colorClass={
                      activePath === 'network'
                        ? 'text-white'
                        : colorsV2.text.light['danger-dark']
                    }
                  >
                    {networkNotificationsCount > 99
                      ? '99+'
                      : networkNotificationsCount}
                  </Text>
                </div>
              </div>
            ) : undefined
          }
        />
      </div>

      {!isAuth && !wasLogged && (
        <ModalPage
          show={show}
          onClose={() => {
            setShow(false)
          }}
        >
          <div className="flex flex-column gap-30">
            <div className={'flex full-w items-center justify-center'}>
              <Avatar
                size={144}
                image={profile?.avatar}
                username={profile?.username}
              />
            </div>
            <Subtitle className="text-center" lineHeightClass="leading-30">
              Sign in to view {profile?.firstName}
              {"'"}s full profile, analytics, network, and {texts.titleEnd}
            </Subtitle>

            {texts.subTitle && (
              <Text className="text-center" weightClass="font-medium">
                {texts.subTitle}
              </Text>
            )}

            <ButtonComponent
              fullWidth
              variation="secondary"
              size="regular"
              label="Sign in"
              onClick={() => navigateToAuth('/login')}
            />

            <Text className="text-center" weightClass="font-medium">
              {texts.buttonsText}
            </Text>

            <ButtonComponent
              fullWidth
              variation="primary"
              size="regular"
              label="JOIN CREATORLAND NOW"
              onClick={() => navigateToAuth('/signup')}
            />
          </div>
        </ModalPage>
      )}
    </>
  )
}
